import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useClientAuth } from '../../auth/ClientAuthContext';
import API_BASE from '../../utils/api';
import PublicStripePaymentModal from '../../components/Projects/Invoices/PublicStripePaymentModal';
import { getTBInvoiceSections, isTBInvoiceListLayout } from '../../utils/timeBillingShared';
import { depositRequestLabel, depositStillDue, formatInvoiceDate } from '../../utils/invoiceAdjustments';

function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

export default function ClientTimeBillingInvoiceDetail() {
  const { projectId, tbInvoiceId } = useParams();
  const { clientAuth } = useClientAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPayment, setShowPayment] = useState(false);

  useEffect(() => {
    if (!clientAuth?.token) return;

    fetch(`${API_BASE}/api/client/projects/${projectId}/timebilling-invoices/${tbInvoiceId}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${clientAuth.token}` },
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
        return data;
      })
      .then(setInvoice)
      .catch((err) => setError(err.message || 'Failed to load invoice.'))
      .finally(() => setLoading(false));
  }, [clientAuth, projectId, tbInvoiceId]);

  if (loading) return <p style={{ color: '#6b7280', fontSize: 14 }}>Loading…</p>;
  if (error) return <><Link to={`/client/projects/${projectId}`} className="cp-back-link">← Back to project</Link><p className="cp-error">{error}</p></>;
  if (!invoice) return null;

  const groups = getTBInvoiceSections(invoice);
  const isListLayout = isTBInvoiceListLayout(invoice);
  const hideEmployeeNames = invoice.display?.hideEmployeeNames;
  const totalAmount = Number(invoice.totals?.total || 0);
  const subtotal = Number(invoice.totals?.subtotal || 0);
  const taxAmount = Number(invoice.totals?.tax || 0);
  const remainingBalance = Math.max(Number((invoice.remainingBalance ?? totalAmount).toFixed(2)), 0);
  const paymentsAmount = Math.max(totalAmount - remainingBalance, 0);
  const isFullyPaid = remainingBalance <= 0;
  const contingencyAmount = Number(invoice.totals?.contingency || 0);
  const discountAmount = Number(invoice.totals?.discount || 0);
  const depositRequested = Number(invoice.totals?.depositRequested || 0);
  const depositDue = isFullyPaid ? 0 : depositStillDue(depositRequested, paymentsAmount);
  const invoiceDate = invoice.invoiceDate || invoice.createdAt;

  const applyPaymentEndpoint = `${API_BASE}/api/client/projects/${projectId}/timebilling-invoices/${tbInvoiceId}/apply-payment`;
  const createIntentEndpoint = `${API_BASE}/api/client/projects/${projectId}/timebilling-invoices/${tbInvoiceId}/create-payment-intent`;

  return (
    <div className="cp-animate">
      <Link to={`/client/projects/${projectId}`} className="cp-back-link">← Back to project</Link>

      <div className="cp-card">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 19, fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.01em' }}>
              {invoice.invoiceNumber ? `Invoice #${invoice.invoiceNumber}` : 'Time & Billing Invoice'}
            </h1>
            {invoiceDate && (
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                Date: {formatInvoiceDate(invoiceDate)}
              </p>
            )}
            {invoice.dueDate && (
              <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>
                Due: {formatInvoiceDate(invoice.dueDate)}
              </p>
            )}
          </div>
          <span style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            background: isFullyPaid ? '#d1fae5' : paymentsAmount > 0 ? '#fef3c7' : '#fee2e2',
            color: isFullyPaid ? '#065f46' : paymentsAmount > 0 ? '#92400e' : '#991b1b',
          }}>
            {isFullyPaid ? 'Paid' : paymentsAmount > 0 ? 'Partially Paid' : 'Unpaid'}
          </span>
        </div>

        {/* Time entry groups */}
        {groups.length > 0 ? (
          groups.map((group) => (
            <div key={group.heading} style={{ marginBottom: 24 }}>
              {group.heading && (
                <div style={{ fontWeight: 600, fontSize: 13.5, paddingBottom: 6, borderBottom: '2px solid #e5e7eb', marginBottom: 8, color: '#111827' }}>
                  {group.heading}
                </div>
              )}
              <table className="cp-table" style={{ marginBottom: 0 }}>
                <thead>
                  <tr>
                    {isListLayout && <th>Activity</th>}
                    {!hideEmployeeNames && <th>Employee</th>}
                    <th>Description</th>
                    <th className="right">Hours</th>
                    <th className="right">Rate</th>
                    <th className="right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(group.items || []).map((item) => (
                    <tr key={item.id}>
                      {isListLayout && <td>{item.activity || '—'}</td>}
                      {!hideEmployeeNames && <td>{item.employee || '—'}</td>}
                      <td>{item.description || '—'}</td>
                      <td className="right">
                        {Number(item.hours).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="right">{formatCurrency(item.rate)}/hr</td>
                      <td className="right" style={{ fontWeight: 500 }}>{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
        ) : (
          <p style={{ color: '#9ca3af', fontSize: 13.5, marginBottom: 24 }}>No time entries recorded.</p>
        )}

        {/* Totals */}
        <div className="cp-totals">
          <div className="cp-totals-inner">
            <div className="cp-total-row"><span>Subtotal</span><span>{formatCurrency(subtotal)}</span></div>
            {contingencyAmount > 0 && (
              <div className="cp-total-row">
                <span>
                  {invoice.contingency?.label || 'Contingency'}
                  {invoice.contingency?.percent ? ` (${invoice.contingency.percent}%)` : ''}
                </span>
                <span>{formatCurrency(contingencyAmount)}</span>
              </div>
            )}
            {discountAmount > 0 && (
              <div className="cp-total-row">
                <span>Discount{invoice.discount?.type === 'percent' ? ` (${invoice.discount.value}%)` : ''}</span>
                <span>−{formatCurrency(discountAmount)}</span>
              </div>
            )}
            {taxAmount > 0 && (
              <div className="cp-total-row">
                <span>Sales Tax{invoice.tax?.location?.name ? ` (${invoice.tax.location.name})` : ''}</span>
                <span>{formatCurrency(taxAmount)}</span>
              </div>
            )}
            <div className="cp-total-row bold"><span>Total</span><span>{formatCurrency(totalAmount)}</span></div>
            {paymentsAmount > 0 && (
              <div className="cp-total-row" style={{ color: '#065f46' }}>
                <span>Payments</span><span>−{formatCurrency(paymentsAmount)}</span>
              </div>
            )}
            {!isFullyPaid && (
              <div className="cp-total-row highlight"><span>Balance Due</span><span>{formatCurrency(remainingBalance)}</span></div>
            )}
            {depositRequested > 0 && (
              <div className="cp-total-row">
                <span>{depositRequestLabel(invoice.depositRequest)}</span>
                <span>{formatCurrency(depositRequested)}</span>
              </div>
            )}
            {depositDue > 0 && (
              <div className="cp-total-row bold"><span>Deposit Due Now</span><span>{formatCurrency(depositDue)}</span></div>
            )}
          </div>
        </div>

        {/* Payment history */}
        {Array.isArray(invoice.paymentHistory) && invoice.paymentHistory.length > 0 && (
          <div className="cp-payment-history">
            <h3>Payment History</h3>
            {invoice.paymentHistory.map((p, i) => (
              <div key={i} className="cp-payment-row">
                <span>
                  {p.date ? new Date(p.date).toLocaleDateString() : '—'}
                  {(p.method || p.paymentType) ? ` · ${p.method || p.paymentType}` : ''}
                </span>
                <span style={{ fontWeight: 600 }}>{formatCurrency(p.amount)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Pay Now action */}
        {!isFullyPaid && (
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 18, marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
            <button className="cp-btn-primary" onClick={() => setShowPayment(true)}>
              Pay Now
            </button>
          </div>
        )}
      </div>

      {/* Stripe payment modal */}
      {showPayment && (
        <PublicStripePaymentModal
          remainingBalance={remainingBalance}
          defaultAmount={depositDue}
          invoiceNumber={invoice.invoiceNumber}
          createIntentEndpoint={createIntentEndpoint}
          applyPaymentEndpoint={applyPaymentEndpoint}
          headers={{ Authorization: `Bearer ${clientAuth.token}` }}
          metadata={{ tbInvoiceId, projectId }}
          onSuccess={(amount, data) => {
            setInvoice((prev) => ({
              ...prev,
              remainingBalance: data?.remainingBalance ?? Math.max((prev.remainingBalance ?? 0) - amount, 0),
            }));
            setShowPayment(false);
          }}
          onClose={() => setShowPayment(false)}
        />
      )}
    </div>
  );
}
